import { z } from "zod";

import { qualitySchema, videoPresetSchema } from "./videoPresets";

/**
 * cameraBearingModeSchema names how a shot's bearing is chosen: `follow` orients
 * the camera along the route's tangent at that point (the camera looks where the
 * path is heading), `locked` holds the route's overall first-to-last heading (a
 * map-like view that does not spin as the path curves), and `value` pins the
 * bearing to the shot's explicit `bearingDeg`. Zod-first because the value
 * crosses the bus boundary from a preset generator, a user control, or an agent
 * camera-edit into the shared Camera Plan.
 */
export const cameraBearingModeSchema = z.enum(["follow", "locked", "value"]);

/**
 * CameraBearingMode names how a shot's bearing is chosen, derived from
 * cameraBearingModeSchema.
 */
export type CameraBearingMode = z.infer<typeof cameraBearingModeSchema>;

/**
 * cameraModeSchema names the camera direction a plan is authored in. `smart`
 * derives framing per keyframe from the local slope and paces the flight into the
 * steeps; `cinematic` is the grounded, reactive follow that banks into corners
 * and dwells on the steepest mid-route beat; `drone` is the high, wide aerial
 * glide; `helicopter` side-tracks the route laterally, broadcast style; `chase`
 * rides low just behind the athlete and accelerates on descents; `overview` is
 * the cartographic reveal — the whole route from near-nadir, a dive down to the
 * trail, and a pull back out; `director` seeds a neutral empty plan an explicit
 * shot list fills. The enum order is the order the mode cards render in, with
 * the org-tier director last. Zod-first because the value is the discriminant
 * the plan and the mode-set tool both read.
 */
export const cameraModeSchema = z.enum([
  "smart",
  "cinematic",
  "drone",
  "helicopter",
  "chase",
  "overview",
  "director",
]);

/**
 * CameraMode names the camera direction a plan is authored in, derived from
 * cameraModeSchema.
 */
export type CameraMode = z.infer<typeof cameraModeSchema>;

/**
 * cameraShotBaseSchema is the no-default field definition for one keyframe in a
 * Camera Plan: it carries the same shape, bounds, and enums as cameraShotSchema
 * but leaves `bearingMode`, `holdMs`, and `speedScale` without their `.default()`
 * so an absent key parses as undefined rather than the default value. It is the
 * source the defaulted cameraShotSchema is derived from, and the base a partial
 * edit input (studioVideoShotSetInput) wraps so a single-field edit carries only
 * that field, never silently restating the defaults over the shot it merges into.
 */
export const cameraShotBaseSchema = z.object({
  at: z.number().min(0).max(1),
  bearingDeg: z.number().min(0).max(360).optional(),
  bearingMode: cameraBearingModeSchema,
  holdMs: z.number().min(0).max(10_000),
  id: z.string(),
  pitchDeg: z.number().min(0).max(85),
  speedScale: z.number().min(0.1).max(10),
  zoom: z.number().min(0).max(22),
});

/**
 * cameraShotSchema is one keyframe in a Camera Plan, anchored by progress along
 * the route (`at` in 0..1) rather than wall-clock, so retiming a plan never
 * invalidates its shots. `pitchDeg` (0..85, MapLibre's tilt ceiling) and `zoom`
 * (0..22) frame the view; `bearingMode` picks how the heading is chosen and
 * `bearingDeg` supplies the explicit value when the mode is `value`; `holdMs`
 * pauses the camera at the shot for an intro/outro beat; `speedScale` retimes
 * the leg leading into the shot (>1 faster, <1 slower) while preserving relative
 * pacing; `id` is a stable handle so a camera-edit tool can target one shot. It
 * is cameraShotBaseSchema with the optional fields given their defaults, so the
 * field definitions live in one place. Zod-first because every field is validated
 * when a preset, a control, or an agent writes a shot, and the bounds reject an
 * out-of-range value at the boundary rather than letting it reach the resolver.
 */
export const cameraShotSchema = cameraShotBaseSchema.extend({
  bearingMode: cameraBearingModeSchema.default("follow"),
  holdMs: z.number().min(0).max(10_000).default(0),
  speedScale: z.number().min(0.1).max(10).default(1),
});

/**
 * CameraShot is one keyframe in a Camera Plan, derived from cameraShotSchema.
 */
export type CameraShot = z.infer<typeof cameraShotSchema>;

/**
 * cameraOutputFormatSchema names the encoder container/codec the recording
 * targets. `mp4` (H.264) is the broadly-compatible default; the two WebM
 * (VP9, VP8) members are reserved for the custom-encoder controls a later
 * surface adds, so the value space is in place from the first plan. Zod-first
 * because it is shared with the output-set tool input.
 */
export const cameraOutputFormatSchema = z.enum(["mp4", "webm-vp9", "webm-vp8"]);

/**
 * CameraOutputFormat names the encoder container/codec, derived from
 * cameraOutputFormatSchema.
 */
export type CameraOutputFormat = z.infer<typeof cameraOutputFormatSchema>;

/**
 * cameraResolutionSchema names the recording's vertical resolution. `1080p` is
 * the default; the taller members are reserved for the custom-encoder controls a
 * later surface adds. Zod-first because it is shared with the output-set tool
 * input.
 */
export const cameraResolutionSchema = z.enum(["720p", "1080p", "1440p", "2160p"]);

/**
 * CameraResolution names the recording's vertical resolution, derived from
 * cameraResolutionSchema.
 */
export type CameraResolution = z.infer<typeof cameraResolutionSchema>;

/**
 * cameraFpsSchema is the recording frame rate, constrained to the three rates
 * the encoder supports (24, 30, 60). Zod-first as a literal union so a value
 * outside the set is rejected at the boundary; shared with the output-set tool
 * input and read by the track resolver to size the per-frame grid.
 */
export const cameraFpsSchema = z.union([z.literal(24), z.literal(30), z.literal(60)]);

/**
 * CameraFps is the recording frame rate, derived from cameraFpsSchema.
 */
export type CameraFps = z.infer<typeof cameraFpsSchema>;

/**
 * cameraOutputBaseSchema is the no-default field definition for the encoder
 * configuration a recording targets: the same shape, bounds, and enums as
 * cameraOutputSchema but with no `.default()` on any field, so an absent key
 * parses as undefined rather than the default value. It is the source the
 * defaulted cameraOutputSchema is derived from, and the base the partial
 * output-set input (studioVideoOutputSetInput) wraps so a single-field
 * reconfiguration carries only that field, never silently restating every default
 * over the plan's current output it merges into. The bitrate ceiling is 45 Mbps,
 * bitrateForOutput's 2160p/60/high tier.
 */
export const cameraOutputBaseSchema = z.object({
  aspect: z.enum(["16:9", "9:16", "1:1"]),
  bitrateMbps: z.number().min(1).max(45).optional(),
  format: cameraOutputFormatSchema,
  fps: cameraFpsSchema,
  preset: videoPresetSchema,
  quality: qualitySchema,
  resolution: cameraResolutionSchema,
});

/**
 * cameraOutputSchema is the encoder configuration a recording targets: the frame
 * `aspect` the wizard map is framed to, an optional `bitrateMbps` override
 * (1..45; absent lets the encoder pick the resolution's default), the container
 * `format`, the `fps`, the vertical `resolution`, the `preset` the output was
 * seeded from (a ready-made template or `custom`), and the `quality` tier the
 * Custom matrix derives its bitrate from. It is cameraOutputBaseSchema with every
 * field given a sensible default, so the field definitions live in one place and a
 * plan parses from an empty object. The defaults match VIDEO_PRESETS.vertical — a
 * fresh plan opens vertical-first (9:16, 1080p H.264 30 fps), so the wizard's
 * checked preset and the framed aspect agree from the first frame rather than the
 * preset disagreeing with the framing; the bitrate is left to the encoder's 1080p
 * default (12 Mbps), which is the Vertical template's bitrate, so a default
 * recording encodes at the preset's rate without the schema carrying a literal.
 * Zod-first because the value is shared with the whole-plan parse paths and
 * validated whenever a control or an agent reconfigures the output.
 */
export const cameraOutputSchema = cameraOutputBaseSchema.extend({
  aspect: z.enum(["16:9", "9:16", "1:1"]).default("9:16"),
  format: cameraOutputFormatSchema.default("mp4"),
  fps: cameraFpsSchema.default(30),
  preset: videoPresetSchema.default("vertical"),
  quality: qualitySchema.default("high"),
  resolution: cameraResolutionSchema.default("1080p"),
});

/**
 * CameraOutput is the encoder configuration a recording targets, derived from
 * cameraOutputSchema.
 */
export type CameraOutput = z.infer<typeof cameraOutputSchema>;

/**
 * cameraPlanSchema is the single shared artifact a recording is built from: the
 * `mode` it was authored in, the encoder `output`, the `shots` keyframe list,
 * the `targetDurationMs` the whole flight runs for (20..120 s), and a `version`
 * literal so a future shape change is detectable. Presets fill `shots`
 * deterministically; the `director` mode seeds an empty list an explicit shot
 * sequence fills. `shots` is kept sorted ascending by `at` as an invariant (see
 * insertShotSorted) so the resolver can walk it without re-sorting per frame.
 * Zod-first because the plan crosses the bus boundary from generators, controls,
 * and agent edits, and lives in the UI store as a validated value.
 */
export const cameraPlanSchema = z.object({
  mode: cameraModeSchema,
  output: cameraOutputSchema.default(() => cameraOutputSchema.parse({})),
  shots: z.array(cameraShotSchema).default([]),
  targetDurationMs: z.number().min(20_000).max(120_000).default(20_000),
  version: z.literal(1).default(1),
});

/**
 * CameraPlan is the single shared artifact a recording is built from, derived
 * from cameraPlanSchema. Its `shots` are kept sorted ascending by `at`.
 */
export type CameraPlan = z.infer<typeof cameraPlanSchema>;

/**
 * routeGeometrySchema is the flattened path a Camera Plan resolves against: the
 * `line` as ordered [lng, lat] pairs (at least two, so a flight has a direction)
 * and the optional matching `elevations` in metres (one per line vertex; absent
 * for a 2D route). Zod-first because it is the validated hand-off shape the route
 * adapter produces and the resolver/generators consume; the line tuple bound
 * rejects a degenerate single-point route at the boundary.
 */
export const routeGeometrySchema = z.object({
  elevations: z.array(z.number()).optional(),
  line: z.array(z.tuple([z.number(), z.number()])).min(2),
});

/**
 * RouteGeometry is the flattened path a Camera Plan resolves against, derived
 * from routeGeometrySchema.
 */
export type RouteGeometry = z.infer<typeof routeGeometrySchema>;

/**
 * CameraPose is the render-free per-frame camera state the resolver produces and
 * the map consumes: the [lng, lat] `center` to look at, the `zoom`, the `pitch`
 * in degrees, and the `bearing` in degrees. It is a hand-written interface (not
 * a schema-derived type) because it is a trusted in-process value passed across
 * the tool seam by the millions per recording — its structural twin in the map
 * package carries the Zod guard at the crossing.
 */
export interface CameraPose {
  bearing: number;
  center: [number, number];
  pitch: number;
  zoom: number;
}

/**
 * insertShotSorted returns a new shot list with `shot` inserted so the list
 * stays sorted ascending by `at` — the invariant cameraPlanSchema documents and
 * the resolver relies on. Ties keep the existing shot ahead of the inserted one
 * (a stable insert), so adding a shot at the same progress as another lands it
 * immediately after. The input list is not mutated.
 */
export const insertShotSorted = (shots: readonly CameraShot[], shot: CameraShot): CameraShot[] => {
  const next = [...shots];
  let index = next.length;
  while (index > 0) {
    const previous = next[index - 1];
    if (previous && previous.at <= shot.at) {
      break;
    }

    index -= 1;
  }
  next.splice(index, 0, shot);

  return next;
};
